
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Paperclip, X, Upload } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { uploadMessageAttachment, validateImageFile, validateFile } from "@/utils/messageAttachments";

interface MessageInputProps {
  onSendMessage: (content: string, attachmentUrl?: string, attachmentName?: string) => void;
  disabled?: boolean;
  conversationId: string;
  currentUserId: string;
}

const MessageInput = ({ onSendMessage, disabled, conversationId, currentUserId }: MessageInputProps) => {
  const [message, setMessage] = useState("");
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pastedImage, setPastedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    checkSubscriptionStatus();
  }, [conversationId, currentUserId]);

  const checkSubscriptionStatus = async () => {
    try {
      setLoading(true);
      
      console.log("Checking subscription status for conversation:", conversationId, "user:", currentUserId);
      
      // Get conversation details to find the other user
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .select('mentor_id, mentee_id')
        .eq('id', conversationId)
        .maybeSingle();

      if (convError) {
        console.error("Error fetching conversation:", convError);
        setIsSubscriptionActive(false);
        setLoading(false);
        return;
      }

      if (!conversation) {
        console.log("No conversation found with ID:", conversationId);
        setIsSubscriptionActive(false);
        setLoading(false);
        return;
      }

      console.log("Found conversation:", conversation);

      // Determine if current user is mentor or mentee
      const isMentor = conversation.mentor_id === currentUserId;
      const menteeId = isMentor ? conversation.mentee_id : currentUserId;
      const mentorId = conversation.mentor_id;

      console.log("User roles - isMentor:", isMentor, "menteeId:", menteeId, "mentorId:", mentorId);

      // Check for active subscription between these users
      // FIXED: Check that the SAME application is both active AND a subscription type
      const { data: applications, error: appError } = await supabase
        .from('applications')
        .select(`
          id,
          is_subscription_active,
          courses!applications_course_id_fkey(course_type)
        `)
        .eq('user_id', menteeId)
        .eq('mentor_id', mentorId)
        .eq('is_subscription_active', true);

      if (appError) {
        console.error("Error checking subscription status:", appError);
        setIsSubscriptionActive(false);
        setLoading(false);
        return;
      }

      console.log("Found applications:", applications);

      // Check if there's an active subscription
      // FIXED: Properly check that the same record has both conditions
      const hasActiveSubscription = applications && applications.length > 0 && 
        applications.some(app => 
          app.is_subscription_active === true && 
          app.courses?.course_type === 'subscription'
        );

      console.log("Has active subscription:", hasActiveSubscription);

      setIsSubscriptionActive(hasActiveSubscription);
    } catch (error) {
      console.error("Exception checking subscription status:", error);
      setIsSubscriptionActive(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    console.log("Paste event triggered!");
    
    const clipboardData = e.clipboardData;
    if (!clipboardData) {
      console.log("No clipboard data available");
      return;
    }

    console.log("Clipboard items:", clipboardData.items.length);
    
    // Check all clipboard items
    for (let i = 0; i < clipboardData.items.length; i++) {
      const item = clipboardData.items[i];
      console.log(`Item ${i}: type=${item.type}, kind=${item.kind}`);
      
      if (item.type.startsWith('image/')) {
        console.log("Found image in clipboard!");
        e.preventDefault();
        
        const file = item.getAsFile();
        if (!file) {
          console.log("Could not get file from clipboard item");
          continue;
        }

        console.log("File details:", { name: file.name, size: file.size, type: file.type });

        const validation = validateImageFile(file);
        if (!validation.valid) {
          toast.error(validation.error);
          return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          console.log("Image preview created");
          setImagePreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
        
        setPastedImage(file);
        toast.success("Screenshot pasted! You can now send it.");
        break;
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    console.log("File selected:", { name: file.name, size: file.size, type: file.type });

    const validation = validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    // Create preview only for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
    
    setPastedImage(file);
    toast.success(`${file.type.startsWith('image/') ? 'Image' : 'File'} selected!`);
  };

  const removePastedImage = () => {
    setPastedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if ((!message.trim() && !pastedImage) || disabled || !isSubscriptionActive) {
      return;
    }

    setIsUploading(true);

    try {
      let attachmentUrl: string | undefined;
      let attachmentName: string | undefined;

      // Upload file if present
      if (pastedImage) {
        console.log("Uploading file attachment...");
        const uploadResult = await uploadMessageAttachment(
          pastedImage, 
          conversationId, 
          currentUserId
        );

        if (!uploadResult.success) {
          console.error("Upload failed:", uploadResult.error);
          toast.error(`Upload failed: ${uploadResult.error}`);
          setIsUploading(false);
          return;
        }

        attachmentUrl = uploadResult.url;
        attachmentName = pastedImage.name || 'Attached file';
        console.log("File uploaded successfully:", attachmentUrl);
      }

      // Send message
      const messageText = message.trim() || (pastedImage ? '' : '');
      onSendMessage(messageText, attachmentUrl, attachmentName);
      
      // Clear form
      setMessage("");
      removePastedImage();
      
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Add global paste event listener as backup
  useEffect(() => {
    const handleGlobalPaste = (e: ClipboardEvent) => {
      // Only handle if the textarea is focused
      if (document.activeElement === textareaRef.current) {
        console.log("Global paste event on textarea");
        // Convert to React event format
        const syntheticEvent = {
          clipboardData: e.clipboardData,
          preventDefault: () => e.preventDefault()
        } as React.ClipboardEvent<HTMLTextAreaElement>;
        
        handlePaste(syntheticEvent);
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
    };
  }, []);

  if (loading) {
    return (
      <div className="p-4 border-t bg-gray-50">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (!isSubscriptionActive) {
    return (
      <div className="p-4 border-t bg-gray-50">
        <div className="flex items-center justify-center h-12 bg-gray-100 rounded-lg">
          <span className="text-gray-500 text-sm">Subscription required - messaging disabled</span>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 border-t bg-white">
      {/* File Preview */}
      {pastedImage && (
        <div className="mb-3 relative inline-block">
          {imagePreview ? (
            <img 
              src={imagePreview} 
              alt="File preview" 
              className="max-w-xs max-h-32 rounded-lg border"
            />
          ) : (
            <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-3 border">
              <Paperclip className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-700">{pastedImage.name}</span>
            </div>
          )}
          <button
            type="button"
            onClick={removePastedImage}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      <div className="flex items-end space-x-2">
        <div className="flex-1">
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={pastedImage ? "Add a message (optional)..." : "Type your message or paste a screenshot (Ctrl+V)..."}
            className="min-h-[40px] max-h-32 resize-none"
            disabled={disabled || !isSubscriptionActive || isUploading}
          />
        </div>
        
        <div className="flex space-x-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={disabled || !isSubscriptionActive || isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          
          <Button
            type="submit"
            size="icon"
            disabled={disabled || (!message.trim() && !pastedImage) || !isSubscriptionActive || isUploading}
            className="bg-emu-blue hover:bg-emu-blue/90"
          >
            {isUploading ? <Upload className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
      />
    </form>
  );
};

export default MessageInput;
